import supabase from "../config/supabase";

const BUCKET_NAME = "DevTeam";

/**
 * Upload a file to Supabase Storage
 * @param {File} file - The file to upload
 * @param {string} folder - Folder path in bucket (e.g., 'chat-attachments', 'screenshots')
 * @returns {Promise<{url: string, filename: string, size: number, contentType: string}>}
 */
export async function uploadToSupabase(file, folder = "chat-attachments") {
  if (!supabase) {
    throw new Error("Supabase is not configured");
  }

  if (!file) {
    throw new Error("No file provided");
  }

  // Generate unique filename with timestamp to prevent collisions
  const timestamp = Date.now();
  const randomId = Math.random().toString(36).substring(2, 9);
  const fileExt = file.name.split(".").pop();
  const filename = `${timestamp}-${randomId}.${fileExt}`;
  const filePath = `${folder}/${filename}`;

  try {
    // Upload file to Supabase Storage
    const { error } = await supabase.storage
      .from(BUCKET_NAME)
      .upload(filePath, file, {
        cacheControl: "3600",
        upsert: false,
      });

    if (error) {
      console.error("Supabase upload error:", error);
      throw new Error(error.message || "Failed to upload file");
    }

    // Get public URL
    const {
      data: { publicUrl },
    } = supabase.storage.from(BUCKET_NAME).getPublicUrl(filePath);

    return {
      url: publicUrl,
      filename: filename,
      size: file.size,
      contentType: file.type,
    };
  } catch (error) {
    console.error("Upload error:", error);
    throw error;
  }
}

/**
 * Delete a file from Supabase Storage
 * @param {string} url - The public URL of the file to delete
 * @returns {Promise<boolean>}
 */
export async function deleteFromSupabase(url) {
  if (!supabase) {
    throw new Error("Supabase is not configured");
  }

  if (!url) {
    return false;
  }

  try {
    // Extract file path from URL
    // URL format: https://[project].supabase.co/storage/v1/object/public/DevTeam/folder/filename.ext
    const urlParts = url.split(`/storage/v1/object/public/${BUCKET_NAME}/`);
    if (urlParts.length < 2) {
      console.error("Invalid Supabase URL format:", url);
      return false;
    }

    const filePath = urlParts[1];

    const { error } = await supabase.storage
      .from(BUCKET_NAME)
      .remove([filePath]);

    if (error) {
      console.error("Supabase delete error:", error);
      return false;
    }

    return true;
  } catch (error) {
    console.error("Delete error:", error);
    return false;
  }
}

/**
 * Upload an image from clipboard paste
 * @param {ClipboardEvent} event - The paste event
 * @returns {Promise<{url: string, filename: string, size: number, contentType: string} | null>}
 */
export async function uploadPastedImage(event) {
  const items = event.clipboardData?.items;
  if (!items) return null;

  for (let i = 0; i < items.length; i++) {
    const item = items[i];

    // Check if item is an image
    if (item.type.indexOf("image") !== -1) {
      const file = item.getAsFile();
      if (file) {
        return await uploadToSupabase(file, "screenshots");
      }
    }
  }

  return null;
}

/**
 * Validate file size and type
 * @param {File} file - The file to validate
 * @param {Object} options - Validation options
 * @param {number} options.maxSize - Max file size in bytes (default: 10MB)
 * @param {string[]} options.allowedTypes - Allowed MIME types
 * @returns {{valid: boolean, error?: string}}
 */
export function validateFile(
  file,
  options = { maxSize: 10 * 1024 * 1024, allowedTypes: [] },
) {
  const { maxSize = 10 * 1024 * 1024, allowedTypes = [] } = options;

  if (!file) {
    return { valid: false, error: "No file provided" };
  }

  if (file.size > maxSize) {
    const maxSizeMB = (maxSize / (1024 * 1024)).toFixed(1);
    return {
      valid: false,
      error: `File size exceeds ${maxSizeMB}MB limit`,
    };
  }

  if (allowedTypes.length > 0 && !allowedTypes.includes(file.type)) {
    return {
      valid: false,
      error: `File type ${file.type} is not allowed`,
    };
  }

  return { valid: true };
}
